import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useEventListener, useTheme, useUnmount } from 'ahooks';
import { Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { MySvgIcon } from '@/components/ui/svg-icon';
import { CALLBACK_MESSAGE, THIRD_PARTY_LOGIN } from '@/constants/config';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCustom } from '@/hooks/use-custom';
import { useFlow } from '@/hooks/use-flow';
import { type CallbackMessage, useSocialLogin } from '@/hooks/use-social-login';
import { useConfigStore } from '@/store/use-config-store';
import { StyledButton } from './components/styled-button';
import { StyledGrid } from './components/styled-grid';

const getSocials = (
  allowedSocials: string[] | undefined,
  enabledSocials: string[] | undefined,
  theme: string,
  searchParams: URLSearchParams,
) => {
  return THIRD_PARTY_LOGIN.filter((social) => {
    const hidden = searchParams.get(social.searchParamsKey);
    if (hidden && hidden !== '1') return false;
    return allowedSocials?.includes(social.thirdName) && enabledSocials?.includes(social.thirdName);
  }).map((item) => ({
    name: item.thirdName,
    iconName: theme === 'dark' ? `${item.svgName}-dark` : item.svgName,
    labelKey: item.i18nKey,
  }));
};

export const ThirdParty: React.FC<{ disabled?: boolean; source: 'login' | 'more' }> = ({ disabled, source }) => {
  const customization = useCustom();
  const enabledSocials = useConfigStore((state) => state.socials);
  const [searchParams] = useSearchParams();
  const [activeSocial, setActiveSocial] = useState<string>('');
  const { runSocialRedirect, runSocialLogin, isOpenWindow, socialWindowRef, socialRedirectLoading, closeSocialWindow } =
    useSocialLogin();
  const { socialAuthRedirecting, setBizState } = useBizState();

  const { theme } = useTheme();
  const { t } = useTranslation();
  const { setFlow } = useFlow();

  const loginSocials = getSocials(customization?.loginPageThirdPartyAuths, enabledSocials, theme, searchParams);

  const moreSocials = getSocials(customization?.moreOptionsThirdPartyAuths, enabledSocials, theme, searchParams);

  const items = source === 'login' ? loginSocials : moreSocials;

  const handleClick = (name: string) => {
    setActiveSocial(name);
    runSocialRedirect(name);
  };

  const handleShowMore = () => {
    setFlow(Flow.LOGIN_SOCIAL_LOGIN_MORE);
  };

  useEventListener('message', (event: MessageEvent) => {
    const { origin, source, data } = event;
    const message = data as CallbackMessage | undefined;

    if (
      origin !== window.location.origin ||
      !isOpenWindow ||
      !socialWindowRef.current ||
      source !== socialWindowRef.current ||
      !message ||
      message.type !== CALLBACK_MESSAGE
    )
      return;

    const callbackData = message.data;

    closeSocialWindow();
    if (!callbackData.code || !callbackData.state) {
      setFlow(Flow.LOGIN_STEP_SOCIAL_ERROR);
    } else {
      runSocialLogin(callbackData);
    }
  });

  useUnmount(closeSocialWindow);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setBizState({ socialAuthRedirecting: false });
        closeSocialWindow();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  return (
    <>
      {source === 'login' && (loginSocials.length > 0 || moreSocials.length > 0) && (
        <Divider aria-label={t('or_divider')} role="">
          {t('or_divider')}
        </Divider>
      )}
      <Grid container spacing={1} direction="column" alignItems="center">
        <StyledGrid size={12}>
          {items.map((item) => (
            <StyledButton
              fullWidth
              key={item.name}
              variant="contained"
              color="secondary"
              loading={socialRedirectLoading && activeSocial === item.name}
              disabled={socialAuthRedirecting || (socialRedirectLoading && activeSocial !== item.name) || disabled}
              onClick={() => handleClick(item.name)}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'relative', zIndex: 2 }}>
                <MySvgIcon name={item.iconName} aria-hidden="true" width="20" height="20" />
                <Typography fontWeight="500">{t(item.labelKey)}</Typography>
              </Stack>
            </StyledButton>
          ))}
        </StyledGrid>

        {source === 'login' && moreSocials.length > 0 && (
          <Grid size={12}>
            <Button
              fullWidth
              variant="text"
              onClick={handleShowMore}
              disabled={socialAuthRedirecting || socialRedirectLoading || disabled}
            >
              {t('more_options')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
};
